// Section 21 "Character sketch": a 6 to 10 line paragraph built from the Ledger's counts with
// templates, in a neutral voice. The sim picks template ids and fills every parameter; the prose
// lives in src/ui/text/en.ts (`sketchText`, 24.14). Six categories always yield a line (outcome,
// people, deprivation, who went short, justice, the gate), so the sketch never has fewer than
// six; optional lines are added in priority order up to ten and then put in reading order.
import type { Config } from '../../config';
import { sortedValues } from '../../core/sorted';
import type { AgeGroup, GameState, LossReason, NormId, SketchLine } from '../../core/types';
import { fateOf, hungryWinters, peakOf, yearOfTurn } from './facts';

export type SketchTemplateId =
  | 'sketch.outcome.won'
  | 'sketch.outcome.sandbox'
  | `sketch.outcome.lost.${LossReason}`
  | 'sketch.outcome.ongoing'
  | 'sketch.people'
  | 'sketch.deprivation.some'
  | 'sketch.deprivation.none'
  | 'sketch.short.none'
  | `sketch.short.most.${AgeGroup}`
  | 'sketch.short.children-never'
  | 'sketch.gate.none-came'
  | 'sketch.gate.none-turned-away'
  | 'sketch.gate.turned-away'
  | 'sketch.gate.starving'
  | 'sketch.justice.none'
  | 'sketch.justice.executed'
  | 'sketch.justice.exiled'
  | 'sketch.justice.flogged.all-left'
  | 'sketch.justice.flogged.some-left'
  | 'sketch.justice.flogged.stayed'
  | 'sketch.justice.lenient'
  | 'sketch.seed.kept-hungry'
  | 'sketch.seed.kept'
  | 'sketch.seed.eaten'
  | 'sketch.chosen.by-hand'
  | 'sketch.chosen.by-hand-none'
  | 'sketch.chosen.policy'
  | 'sketch.norm.established'
  | 'sketch.norm.established-announced'
  | 'sketch.norm.none'
  | 'sketch.festival.hungry'
  | 'sketch.festival.held'
  | 'sketch.child-labour'
  | 'sketch.knowledge.lost'
  | 'sketch.knowledge.literacy'
  | 'sketch.knowledge.improvements'
  | 'sketch.inequality.peak'
  | 'sketch.inequality.even'
  | 'sketch.fragility.peak'
  | 'sketch.founders-club'
  | 'sketch.departures'
  | 'sketch.youngest-death'
  | 'sketch.youngest-death.infant';

export const SKETCH_MIN_LINES = 6;
export const SKETCH_MAX_LINES = 10;

type Params = Record<string, string | number>;

interface Candidate {
  /** Reading order in the paragraph. */
  order: number;
  template: SketchTemplateId;
  params: Params;
}

export interface SketchFacts {
  deprivationDeaths: number;
  shortShareByGroup: Record<AgeGroup, number>;
  finalLiteracy: number;
  foundersClub: boolean;
  steadyTarget: number;
}

const line = (order: number, template: SketchTemplateId, params: Params = {}): Candidate => ({
  order,
  template,
  params,
});

function outcomeLine(state: GameState, cfg: Config, facts: SketchFacts): Candidate {
  const spy = cfg.people.seasonsPerYear;
  const o = state.outcome;
  if (o === null)
    return line(0, 'sketch.outcome.ongoing', {
      year: yearOfTurn(state.turn, spy),
      seasons: state.turn,
    });
  const year = yearOfTurn(o.turn, spy);
  if (o.kind === 'lost') return line(0, `sketch.outcome.lost.${o.reason}`, { year });
  if (state.sandbox)
    return line(0, 'sketch.outcome.sandbox', {
      target: state.options.steadyTargetYears,
      year,
      counter: state.steady.counter,
    });
  return line(0, 'sketch.outcome.won', { target: facts.steadyTarget, year });
}

function shortLine(state: GameState, facts: SketchFacts): Candidate {
  const ps = state.ledger.personSeasons;
  const groups: AgeGroup[] = ['child', 'adult', 'elder'];
  const anyShort = groups.some((g) => ps[g].short > 0);
  if (!anyShort) return line(20, 'sketch.short.none');
  let worst: AgeGroup = 'child';
  for (const g of groups)
    if (facts.shortShareByGroup[g] > facts.shortShareByGroup[worst]) worst = g;
  const percent = Math.max(1, Math.round(facts.shortShareByGroup[worst] * 100));
  return line(20, `sketch.short.most.${worst}`, { percent });
}

function gateLine(state: GameState): Candidate {
  const arrivals = state.ledger.arrivals;
  if (arrivals.length === 0) return line(70, 'sketch.gate.none-came');
  const starving = arrivals.filter((a) => a.decision === 'turn-away' && a.starvingFamily).length;
  if (starving > 0) return line(70, 'sketch.gate.starving', { count: starving });
  const turnedAway = arrivals.filter((a) => a.decision === 'turn-away').length;
  if (turnedAway === 0) return line(70, 'sketch.gate.none-turned-away');
  return line(70, 'sketch.gate.turned-away', { count: turnedAway, total: arrivals.length });
}

/** The harshest justice line: executions, then exiles, then floggings and their aftermath. */
function justiceLines(state: GameState, cfg: Config): Candidate[] {
  const acc = state.ledger;
  const cases = sortedValues(state.cases).filter((c) => c.response !== null);
  const out: Candidate[] = [];
  if (acc.executions > 0) out.push(line(60, 'sketch.justice.executed', { count: acc.executions }));
  if (acc.exiles > 0) out.push(line(61, 'sketch.justice.exiled', { count: acc.exiles }));
  const flogged = cases.filter((c) => c.response === 'flogging');
  if (flogged.length > 0) {
    const withinYear = cfg.people.seasonsPerYear;
    const left = flogged.filter((c) => {
      const f = fateOf(state, c.offenderId);
      return (f.fate === 'left' || f.fate === 'exiled') && (f.turn ?? 0) - c.turn <= withinYear;
    }).length;
    const count = flogged.length;
    if (left === count) out.push(line(62, 'sketch.justice.flogged.all-left', { count }));
    else if (left === 0) out.push(line(62, 'sketch.justice.flogged.stayed', { count }));
    else out.push(line(62, 'sketch.justice.flogged.some-left', { count, left }));
  } else if (acc.corporalPunishments > 0) {
    out.push(line(62, 'sketch.justice.flogged.stayed', { count: acc.corporalPunishments }));
  }
  if (out.length > 0) return out;
  if (cases.length > 0) return [line(60, 'sketch.justice.lenient', { count: cases.length })];
  return [line(60, 'sketch.justice.none')];
}

function seedLine(state: GameState): Candidate | null {
  const eaten = state.ledger.seedEaten.length;
  if (eaten > 0) return line(30, 'sketch.seed.eaten', { count: eaten });
  const winters = hungryWinters(state);
  if (winters > 0) return line(30, 'sketch.seed.kept-hungry', { winters });
  return state.turn >= 4 ? line(30, 'sketch.seed.kept') : null;
}

function chosenLine(state: GameState): Candidate | null {
  const decisions = state.ledger.shortageDecisions;
  const byHand = decisions.filter((d) => d.byHand);
  if (byHand.length > 0) {
    const none = new Set(byHand.flatMap((d) => d.pinnedNone)).size;
    return none > 0
      ? line(25, 'sketch.chosen.by-hand-none', { count: byHand.length, none })
      : line(25, 'sketch.chosen.by-hand', { count: byHand.length });
  }
  const ps = state.ledger.personSeasons;
  const shortages = decisions.length > 0 || ps.child.short + ps.adult.short + ps.elder.short > 0;
  return shortages ? line(25, 'sketch.chosen.policy') : null;
}

function normLine(state: GameState, cfg: Config): Candidate | null {
  const first = state.ledger.normEvents.find((e) => e.change === 'established');
  if (first === undefined) return state.turn >= 8 ? line(50, 'sketch.norm.none') : null;
  const norm: NormId = first.normId;
  const year = yearOfTurn(first.turn, cfg.people.seasonsPerYear);
  return line(
    50,
    first.announced ? 'sketch.norm.established-announced' : 'sketch.norm.established',
    { norm, year },
  );
}

function festivalLine(state: GameState): Candidate | null {
  const festivals = state.ledger.festivals;
  if (festivals.length === 0) return null;
  const hungry = festivals.filter((f) => f.hungryYear).length;
  return hungry > 0
    ? line(35, 'sketch.festival.hungry', { count: hungry })
    : line(35, 'sketch.festival.held', { count: festivals.length });
}

function knowledgeLine(state: GameState, facts: SketchFacts): Candidate | null {
  const acc = state.ledger;
  const lost = new Set(acc.skillsLost.map((s) => s.skill)).size;
  if (lost > 0) return line(80, 'sketch.knowledge.lost', { count: lost });
  if (acc.improvements.length > 0)
    return line(80, 'sketch.knowledge.improvements', { count: acc.improvements.length });
  if (state.turn >= 8)
    return line(80, 'sketch.knowledge.literacy', {
      percent: Math.round(facts.finalLiteracy * 100),
    });
  return null;
}

function inequalityLine(state: GameState, cfg: Config): Candidate | null {
  const { peak, turn } = peakOf(state.series.gini);
  if (state.series.gini.length === 0) return null;
  if (peak < cfg.ledger.egalitarianGini) return line(85, 'sketch.inequality.even');
  return line(85, 'sketch.inequality.peak', {
    gini: peak.toFixed(2),
    year: yearOfTurn(turn, cfg.people.seasonsPerYear),
  });
}

function fragilityLine(state: GameState, cfg: Config): Candidate | null {
  if (state.series.fragility.length === 0) return null;
  const { peak, turn } = peakOf(state.series.fragility);
  return line(90, 'sketch.fragility.peak', {
    year: yearOfTurn(turn, cfg.people.seasonsPerYear),
    value: Math.round(peak),
  });
}

function youngestLine(state: GameState, cfg: Config): Candidate | null {
  let youngest: (typeof state.ledger.deaths)[number] | null = null;
  for (const d of state.ledger.deaths)
    if (youngest === null || d.ageSeasons < youngest.ageSeasons) youngest = d;
  if (youngest === null) return null;
  const years = Math.floor(youngest.ageSeasons / cfg.people.seasonsPerYear);
  return years === 0
    ? line(95, 'sketch.youngest-death.infant', { name: youngest.name })
    : line(95, 'sketch.youngest-death', { name: youngest.name, age: years });
}

function departuresLine(state: GameState): Candidate | null {
  const left = state.ledger.departures.filter((d) => d.reason !== 'exile').length;
  return left > 0 ? line(75, 'sketch.departures', { count: left }) : null;
}

export function characterSketch(state: GameState, cfg: Config, facts: SketchFacts): SketchLine[] {
  const acc = state.ledger;
  const required: Candidate[] = [
    outcomeLine(state, cfg, facts),
    line(10, 'sketch.people', {
      births: acc.births,
      deaths: acc.deaths.length,
      peak: acc.peakPopulation.value,
    }),
    facts.deprivationDeaths > 0
      ? line(15, 'sketch.deprivation.some', { count: facts.deprivationDeaths })
      : line(15, 'sketch.deprivation.none'),
    shortLine(state, facts),
    gateLine(state),
  ];
  const justice = justiceLines(state, cfg);
  const [firstJustice, ...moreJustice] = justice;
  if (firstJustice !== undefined) required.push(firstJustice);
  const ps = acc.personSeasons;
  const optional: (Candidate | null)[] = [
    seedLine(state),
    chosenLine(state),
    festivalLine(state),
    acc.childLabourSeasons > 0
      ? line(40, 'sketch.child-labour', { count: acc.childLabourSeasons })
      : null,
    ps.child.total > 0 && ps.child.short === 0 && ps.adult.short + ps.elder.short > 0
      ? line(22, 'sketch.short.children-never')
      : null,
    normLine(state, cfg),
    ...moreJustice,
    facts.foundersClub ? line(72, 'sketch.founders-club') : null,
    departuresLine(state),
    knowledgeLine(state, facts),
    inequalityLine(state, cfg),
    fragilityLine(state, cfg),
    youngestLine(state, cfg),
  ];
  const chosen = [...required];
  for (const c of optional) {
    if (chosen.length >= SKETCH_MAX_LINES) break;
    if (c !== null) chosen.push(c);
  }
  return chosen
    .sort((a, b) => a.order - b.order)
    .map((c) => ({ template: c.template, params: c.params }));
}
